import { useEffect } from 'react';
import PropTypes from 'prop-types';

import { Navigate } from 'react-router';

import { connect } from 'react-redux';
import { useAppDispatch as useDispatch } from 'dux/app/hooks';

import noop from 'lodash/fp/noop';

import { ProgressTracker } from 'Components/ProgressTracker';

import * as UserService from 'Services/UserService';

import { useHomepageEmailCapturePopUpAbTest } from 'abTesting/homepageEmailCapturePopUp/hook';

import consultationCategoryProp from 'PropTypes/consultationProps';
import consultationSubsetCategoryProp from 'PropTypes/consultationSubsetProps';

import ConsultationMagicLinkSent from '../Scenes/ConsultationMagicLinkSent';
import Error from '../Scenes/Error';
import Signup from '../Scenes/Signup';

import { formatSignupUserFromFormikToApi, setSignupFormikFieldError } from '../utils';

import * as userActions from 'dux/user/actions';
import * as authSelectors from 'dux/auth/selectors';
import * as consultationSelectors from 'dux/consultation/selectors';
import * as signupSelectors from 'dux/signup/selectors';
import { getHasSignedUpWithoutNames, getUserEmail } from 'dux/user/selectors';
import { VARIANT1 } from 'dux/featureFlags/constants/flagVariants';
import {
  apiSignupOrigin,
  heapSignupOrigin,
  heapSignupSubOrigin,
  signupUser,
} from 'dux/signup/thunks';

export const ConsultationSignup = ({
  clearReferral,
  consultationCategory,
  consultationSubsetCategory,
  error,
  invalidCoupon,
  isAuthenticated,
  isAuthenticating,
  magicLinkSent,
  onNext,
  content,
  questionIndex,
  firstname,
  routeParams,
  hasSignedUpWithoutNames,
  email,
}) => {
  const dispatch = useDispatch();
  const hasSigninFirstParam = routeParams?.get('signin-first') === 'true';

  const { variantName: homepageEmailCapturePopUpAbTestVariant } =
    useHomepageEmailCapturePopUpAbTest();
  const isInHomepageEmailCaptureExperiment =
    hasSignedUpWithoutNames && homepageEmailCapturePopUpAbTestVariant === VARIANT1;

  useEffect(() => {
    // on unmount, clear coupon from session if invalid
    return invalidCoupon ? clearReferral : noop;
  }, [invalidCoupon, clearReferral]);

  useEffect(() => {
    if (
      !isInHomepageEmailCaptureExperiment &&
      !error &&
      !invalidCoupon &&
      !magicLinkSent &&
      !isAuthenticating &&
      isAuthenticated
    ) {
      onNext();
    }
  }, [
    error,
    invalidCoupon,
    magicLinkSent,
    isAuthenticating,
    isAuthenticated,
    isInHomepageEmailCaptureExperiment,
  ]);

  if (error) {
    return <Error />;
  }

  if (invalidCoupon) {
    return <Navigate replace to="/redeem/invalid-code" />;
  }

  if (magicLinkSent) {
    /* Within the consultation we want to show the progress tracker for better UX */
    return (
      <ConsultationMagicLinkSent
        background="beige"
        consultationCategory={consultationCategory}
        firstname={firstname}
      >
        <ProgressTracker
          consultationCategory={consultationCategory}
          consultationSubsetCategory={consultationSubsetCategory}
          questionIndex={questionIndex}
          routeParams={routeParams}
        />
      </ConsultationMagicLinkSent>
    );
  }

  return (
    <Signup
      content={content}
      handleSubmit={async (values, actions) => {
        if (isInHomepageEmailCaptureExperiment) {
          actions.setSubmitting(true);
          await UserService.update({
            first_name: values.firstName,
            last_name: values.lastName,
          });
          await dispatch(userActions.fetchUser());
          actions.setSubmitting(false);
        } else {
          dispatch(
            signupUser({
              // fist param: user object with the backend shape
              user: formatSignupUserFromFormikToApi(values),
              // second param: callbacks to react and formik
              actions: {
                ...actions,
                setFieldError: setSignupFormikFieldError(actions),
              },
              consultationCategory,
              origin: hasSigninFirstParam
                ? apiSignupOrigin.NO_SIGN_IN_LINK_RECEIVED[consultationCategory]
                : apiSignupOrigin.CONSULTATION[consultationCategory],
              heapOrigin: hasSigninFirstParam
                ? heapSignupOrigin.NO_SIGN_IN_LINK_RECEIVED
                : heapSignupOrigin.CONSULTATION,
              heapSubOrigin: heapSignupSubOrigin[consultationCategory],
            })
          );
        }
      }}
      initialValues={{
        firstName: '',
        lastName: '',
        email,
      }}
      questionIndex={questionIndex}
    />
  );
};

ConsultationSignup.defaultProps = {
  consultationSubsetCategory: null,
  firstname: '',
  email: '',
  routeParams: null,
};

ConsultationSignup.propTypes = {
  clearReferral: PropTypes.func.isRequired,
  consultationCategory: consultationCategoryProp.isRequired,
  consultationSubsetCategory: consultationSubsetCategoryProp,
  error: PropTypes.bool.isRequired,
  invalidCoupon: PropTypes.bool.isRequired,
  hasSignedUpWithoutNames: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  isAuthenticating: PropTypes.bool.isRequired,
  magicLinkSent: PropTypes.bool.isRequired,
  onNext: PropTypes.func.isRequired,
  content: PropTypes.shape({}).isRequired,
  questionIndex: PropTypes.number.isRequired,
  firstname: PropTypes.string,
  email: PropTypes.string,
  routeParams: PropTypes.any,
};

export default connect(
  (state, { consultationCategory, consultationSubsetCategory, questionIndex, routeParams }) => ({
    hasSignedUpWithoutNames: getHasSignedUpWithoutNames(state),
    error: signupSelectors.getHasError(state),
    invalidCoupon: signupSelectors.getIsInvalidCoupon(state),
    isAuthenticated: authSelectors.getIsAuthenticated(state),
    isAuthenticating: authSelectors.getIsAuthenticating(state),
    magicLinkSent: signupSelectors.getIsMagicLinkSent(state),
    firstname: signupSelectors.getFirstname(state),
    email: getUserEmail(state),
    content: consultationSelectors.getConsultationQuestionForIndex(state, {
      consultationCategory,
      consultationSubsetCategory,
      questionIndex,
      routeParams,
    }),
  }),
  {
    clearReferral: userActions.clearReferral,
  }
)(ConsultationSignup);
